/* oxlint-disable react-doctor/rendering-hydration-mismatch-time */
import { usePostHog } from '@posthog/react';
import { buttonStyle } from 'components/buttons/buttonStyle';
import { CallForActionButtonTooltip } from 'components/buttons/CallForActionButton';
import MenuButton, { MenuButtonItem } from 'components/buttons/MenuButton';
import { NEW_DESIGN_CREATE_CLASS } from 'components/buttons/ListCreateButton';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import DateRangePickerModal from 'components/modals/date-range/DateRangePickerModal';
import { useModal } from 'components/modals/useModal';
import PermissionGuard from 'components/security/PermissionGuard';
import { Button } from 'components/ui/button';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useGetBillableMattersCountQuery } from 'store/api/billable-matters';
import { useCreateDraftInvoicesMutation } from 'store/api/invoices';
import { ERROR_CODE_MESSAGES } from 'store/api/portalAPI';
import { FeatureFlag } from 'types/features';
import { ErroredMatterDto } from 'types/invoices';
import { errorAlert, successAlert, warningAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';

type Props = {
    matterId: string;
};

const MatterNewInvoiceButton = ({ matterId }: Props) => {
    const posthog = usePostHog();
    const isNewDesign = useIsNewDesign();
    const navigate = useNavigate();
    const { openModal, closeModal } = useModal();
    const isDateRangeEnabled = useIsFeatureEnabled(FeatureFlag.PRESELECT_TC_EXP_MANUAL_INVOICE);
    const [createDrafts, { isLoading }] = useCreateDraftInvoicesMutation();
    const { data: billableCount, isSuccess: isBillableCountResolved } = useGetBillableMattersCountQuery(
        { filters: `id==${matterId};matterStats=isbillable=true` },
        { refetchOnMountOrArgChange: true }
    );
    const isBillable = (billableCount?.count ?? 0) > 0;

    // `=isbillable=` folds two unrelated reasons into one flag — the case has nothing to invoice, and an
    // open invoice blocks a second one — so a single message can only be right about one of them. The
    // second count asks the backend which it is, off the same SQL fragment `=isbillable=` derives its
    // blocking conjunct from, and is only worth asking once the case is known not to be billable.
    const { data: blockingInvoiceCount, isSuccess: isBlockingInvoiceCountResolved } = useGetBillableMattersCountQuery(
        { filters: `id==${matterId};matterStats=isblockedbyopeninvoice=true` },
        { refetchOnMountOrArgChange: true, skip: !isBillableCountResolved || isBillable }
    );
    const disabledReasonId = !isBlockingInvoiceCountResolved
        ? null
        : (blockingInvoiceCount?.count ?? 0) > 0
          ? 'invoices.new-invoice-disabled-tooltip'
          : 'invoices.new-invoice-nothing-to-invoice-tooltip';

    const resolveMatterErrorReason = (erroredMatter: ErroredMatterDto): string | null => {
        const codeErrorId = erroredMatter.code ? ERROR_CODE_MESSAGES.get(erroredMatter.code) : undefined;
        if (codeErrorId) {
            return t(`error-code-message.${codeErrorId}`);
        }

        return erroredMatter.message;
    };

    const handleCreateDraftInvoice = async (dateFrom: string | null, dateTo: string | null) => {
        await createDrafts({ matterIds: [Number(matterId)], startDate: dateFrom, endDate: dateTo })
            .unwrap()
            .then((resp) => {
                if (resp.createdInvoices.length) {
                    posthog.capture('invoice_created', {
                        type: 'draft',
                        invoice_count: resp.createdInvoices.length,
                    });
                }
                resp.erroredMatters.forEach((erroredMatter) => {
                    const matterName = erroredMatter.matterName ?? `${erroredMatter.matterId}`;
                    const reason = resolveMatterErrorReason(erroredMatter);
                    warningAlert(
                        reason
                            ? t('invoices.error-creating-invoice-matter', { matterName, reason })
                            : t('invoices.error-creating-invoice-matter-id', {
                                  matterId: `${erroredMatter.matterId}`,
                              })
                    );
                });
                if (resp.createdInvoices.length === 1) {
                    successAlert(t('invoices.single-invoice-draft-created'));
                    navigate(`/invoice/${resp.createdInvoices[0]}`);
                }
            })
            .catch((error) => {
                console.error(error);
                errorAlert(t('invoices.error-creating-invoices'));
            });
    };

    const openDateRangeModal = () => {
        openModal(
            <DateRangePickerModal
                onCancel={closeModal}
                saveButtonTextId="proceed"
                maxDateTo={new Date()}
                atLeastOneRequired
                showPreviousMonthQuickPick
                onSave={(dateFrom, dateTo) => {
                    closeModal();
                    void handleCreateDraftInvoice(dateFrom, dateTo);
                }}
            />,
            closeModal
        );
    };

    const menuItems: MenuButtonItem[] = [
        {
            label: t('invoices.full-time-range'),
            onClick: () => {
                void handleCreateDraftInvoice(null, null);
            },
        },
        {
            label: t('invoices.limited-time-range'),
            onClick: openDateRangeModal,
        },
    ];

    const isDisabled = !isBillable || isLoading;

    return (
        <PermissionGuard
            permissions={['CREATE.INVOICES.MATTER_ACCESS', 'CREATE.INVOICES.TYPE_DEBIT_WITH_MATTER_ACCESS']}
        >
            <CallForActionButtonTooltip title={disabledReasonId ? t(disabledReasonId) : undefined}>
                <div>
                    {isDateRangeEnabled ? (
                        <MenuButton
                            variant="contained"
                            color="secondary"
                            size="small"
                            className={isNewDesign ? NEW_DESIGN_CREATE_CLASS : undefined}
                            disabled={isDisabled}
                            menuItems={menuItems}
                        >
                            <FormattedMessage id={isNewDesign ? 'new-entity.invoice' : 'invoices.new-invoice'} />
                        </MenuButton>
                    ) : (
                        <Button
                            {...buttonStyle(
                                { variant: 'contained', color: 'secondary', size: 'small' },
                                isNewDesign ? NEW_DESIGN_CREATE_CLASS : undefined
                            )}
                            disabled={isDisabled}
                            onClick={() => {
                                void handleCreateDraftInvoice(null, null);
                            }}
                        >
                            <FormattedMessage id={isNewDesign ? 'new-entity.invoice' : 'invoices.new-invoice'} />
                        </Button>
                    )}
                </div>
            </CallForActionButtonTooltip>
        </PermissionGuard>
    );
};

export default MatterNewInvoiceButton;
